import React, { useEffect, useMemo, useState } from "react";
import InstanceTransformInspector from "./InstanceTransformInspector";
import InstanceParameterBindingsInspector from "./InstanceParameterBindingsInspector";
import InstanceParameterOverridesInspector from "./InstanceParameterOverridesInspector";
import interiorLibraryAdminApi from "../../services/interiorLibraryAdminApi";
import interiorParameterApi from "../../services/interiorParameterApi";

const HIDDEN_PREFIXES = ["parent_", "global_", "project_"];

function parseValue(value) {
  const raw = String(value ?? "").trim();
  if (raw === "") return "";
  const n = Number(raw);
  return Number.isFinite(n) ? n : value;
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap ${
        active
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

function ProjectParametersPanel({ projectParameters, setProjectParameters }) {
  const [definitions, setDefinitions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    async function loadMeta() {
      const [defsRes, matsRes] = await Promise.all([
        interiorParameterApi.listDefinitions(),
        interiorLibraryAdminApi.listMaterials(),
      ]);

      setDefinitions(defsRes.items || []);
      setMaterials(matsRes.items || []);
    }

    loadMeta();
  }, []);

  const definitionMap = useMemo(() => {
    return new Map((definitions || []).map((d) => [d.param_key, d]));
  }, [definitions]);

  function updateParam(key, value) {
    setProjectParameters((prev) => ({
      ...prev,
      [key]: parseValue(value),
    }));
  }

  function addParam() {
  setShowAddModal(true);
}

function addSelectedProjectParam(key) {
  const def = definitionMap.get(key);
  if (!def) return;

  setProjectParameters((prev) => ({
    ...prev,
    [key]: def.default_value ?? "",
  }));

  setShowAddModal(false);
}

  function removeParam(key) {
    const ok = window.confirm(`Remove project parameter "${key}"?`);
    if (!ok) return;

    setProjectParameters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function renderValueInput(key, value) {
    const def = definitionMap.get(key);

    if (def?.data_type === "material") {
      return (
        <select
          value={value ?? ""}
          onChange={(e) => updateParam(key, e.target.value)}
          className="w-full px-3 py-2 border rounded text-sm bg-white"
        >
          <option value="">No material</option>
          {materials.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        value={value}
        onChange={(e) => updateParam(key, e.target.value)}
        className="w-full px-3 py-2 border rounded text-sm"
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Project Parameters</h3>
        <div className="text-xs text-gray-500">
          Global values available everywhere as project_ and global_ aliases.
        </div>
      </div>

      <div className="border rounded p-3 bg-blue-50 space-y-3">
  <div>
    <div className="font-semibold text-sm">Project material preset</div>
    <div className="text-xs text-gray-500">
      Globalni default materijali za ceo projekat.
    </div>
  </div>

 {definitions
  .filter((d) => {
    const scopes = Array.isArray(d.allowed_scopes)
      ? d.allowed_scopes
      : JSON.parse(d.allowed_scopes || "[]");

    return (
      d.data_type === "material" &&
      scopes.includes("project")
    );
  })
  .map((d) => (
    <div key={d.id}>
      <label className="text-xs text-gray-500">
        {d.label || d.param_key}
      </label>

      <select
        value={projectParameters?.[d.param_key] ?? ""}
        onChange={(e) =>
          updateParam(d.param_key, e.target.value)
        }
        className="w-full px-3 py-2 border rounded text-sm bg-white"
      >
        <option value="">No material</option>

        {materials.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
    </div>
  ))}
</div>

      <div className="space-y-2">
        {Object.entries(projectParameters || {}).map(([key, value]) => (
          <div key={key} className="border rounded p-3 bg-white">
            <div className="flex justify-between gap-2 mb-1">
              <label className="text-xs font-medium text-gray-500">
                {definitionMap.get(key)?.label || key}
              </label>
              <button
                type="button"
                onClick={() => removeParam(key)}
                className="text-xs text-red-600"
              >
                remove
              </button>
            </div>

            {renderValueInput(key, value)}

            <div className="mt-1 text-[11px] text-gray-400">
              Key: <code>{key}</code> · Use as: <code>project_{key}</code>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addParam}
        className="w-full px-3 py-2 rounded border bg-white text-sm"
      >
        + Add project parameter
      </button>

      {showAddModal && (
  <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
    <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[80vh] overflow-auto p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold">Add project parameter</div>
          <div className="text-xs text-gray-500">
            Select parameter from registry.
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowAddModal(false)}
          className="text-gray-500"
        >
          ✕
        </button>
      </div>

      <select
        className="w-full px-3 py-2 border rounded bg-white"
        defaultValue=""
        onChange={(e) => addSelectedProjectParam(e.target.value)}
      >
        <option value="" disabled>
          Select parameter...
        </option>

        {definitions
          .filter((d) => {
            const scopes = Array.isArray(d.allowed_scopes)
              ? d.allowed_scopes
              : JSON.parse(d.allowed_scopes || "[]");

            return (
              scopes.includes("project") &&
              projectParameters?.[d.param_key] === undefined
            );
          })
          .map((d) => (
            <option key={d.id} value={d.param_key}>
              {d.label || d.param_key}
            </option>
          ))}
      </select>
    </div>
  </div>
)}
    </div>
  );
}

function ResolvedView({ resolvedNode }) {
  const visibleParams = useMemo(() => {
  const params = resolvedNode?.effectiveParameters || {};
  const ownKeys = resolvedNode?.ownParameterKeys || [];

  return Object.entries(params).filter(([key]) => {
    if (HIDDEN_PREFIXES.some((prefix) => key.startsWith(prefix))) return false;

    if (ownKeys.length) {
      return ownKeys.includes(key);
    }

    return true;
  });
}, [resolvedNode]);

  if (!resolvedNode) {
    return (
      <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
        Select an element in the scene or hierarchy.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Resolved Parameters</h3>
        <div className="text-xs text-gray-500">
          Final values used by geometry and child elements.
        </div>
      </div>

      <div className="border rounded bg-white overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b">
          <div className="font-medium text-sm">{resolvedNode.name}</div>
          <div className="text-xs text-gray-500">
            {resolvedNode.category || resolvedNode.nodeType || "node"}
          </div>
        </div>

        {visibleParams.length ? (
          <div className="divide-y">
            {visibleParams.map(([key, value]) => (
              <div
                key={key}
                className="flex justify-between gap-3 px-3 py-2 text-sm"
              >
                <span className="text-gray-600">{key}</span>
                <span className="font-mono text-gray-900">{String(value)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-3 text-sm text-gray-500">
            No visible resolved parameters.
          </div>
        )}
      </div>
    </div>
  );
}

function ParameterFlowView({ resolvedNode }) {
  const dbg = resolvedNode?.parameterDebug;

  if (!dbg) {
    return (
      <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
        Select an element to inspect parameter flow.
      </div>
    );
  }

  const groups = [
    ["Project / Global", dbg.globalParameters],
    ["Parent", dbg.parentParameters],
    ["Definition defaults", dbg.definitionDefaults],
    ["Instance bindings", dbg.parameterBindings],
    ["Instance overrides", dbg.parameterOverrides],
    ["Constraints", dbg.constraints],
    ["Effective result", dbg.effectiveParameters],
  ];

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-semibold">Parameter Flow</h3>
        <div className="text-xs text-gray-500">
          This shows how values are inherited through all depths.
        </div>
      </div>

      {groups.map(([title, obj]) => (
        <details
          key={title}
          className="border rounded bg-white"
          open={title === "Effective result"}
        >
          <summary className="px-3 py-2 cursor-pointer text-sm font-medium bg-gray-50">
            {title}
          </summary>

          <div className="divide-y">
            {Object.entries(obj || {}).length ? (
              Object.entries(obj || {}).map(([key, value]) => (
                <div
                  key={key}
                  className="flex justify-between gap-3 px-3 py-2 text-xs"
                >
                  <span className="text-gray-600 break-all">{key}</span>
                  <span className="font-mono text-gray-900 break-all">
                    {String(value)}
                  </span>
                </div>
              ))
            ) : (
              <div className="p-3 text-xs text-gray-500">Empty</div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function PartitionsPanel({ node, resolvedNode, onChange }) {
  const category = String(
    resolvedNode?.category ||
      resolvedNode?.definition?.category ||
      node?.category ||
      node?.definition?.category ||
      ""
  ).toLowerCase();

  const [partitionSide, setPartitionSide] = useState("front");

  const name = String(resolvedNode?.name || node?.name || "").toLowerCase();

  const isCabinet =
    category.includes("cabinet.box") || name.includes("cabinet box");

  if (!node || !isCabinet) {
    return (
      <div className="text-sm text-gray-500">
        Select a cabinet box to edit partitions.
      </div>
    );
  }

 const partitions = node.partitions || {};

const activePartition = partitions[partitionSide] || {
  horizontal: [],
  vertical: [],
  panelOverrides: {},
};

const params = node.parameterOverrides || {};

function updateCabinetParam(key, value) {
  onChange?.({
    id: node.id,
    parameterOverrides: {
      ...(node.parameterOverrides || {}),
      [key]: parseValue(value),
    },
  });
}

  function update(next) {
    onChange?.({
      ...node,
      partitions: next,
    });
  }

  function addHorizontal() {
  update({
    ...partitions,
    [partitionSide]: {
      ...activePartition,
      horizontal: [
        ...(activePartition.horizontal || []),
        { id: Date.now(), offset: 100 },
      ],
    },
  });
}

  function removeHorizontal(id) {
  update({
    ...partitions,
    [partitionSide]: {
      ...activePartition,
      horizontal: (activePartition.horizontal || []).filter((l) => l.id !== id),
    },
  });
}

  function updateOffset(id, value) {
  update({
    ...partitions,
    [partitionSide]: {
      ...activePartition,
      horizontal: (activePartition.horizontal || []).map((l) =>
        l.id === id ? { ...l, offset: Number(value) } : l
      ),
    },
  });
}

  function addVertical() {
  update({
    ...partitions,
    [partitionSide]: {
      ...activePartition,
      vertical: [
        ...(activePartition.vertical || []),
        { id: Date.now(), offset: 100 },
      ],
    },
  });
}

  function removeVertical(id) {
  update({
    ...partitions,
    [partitionSide]: {
      ...activePartition,
      vertical: (activePartition.vertical || []).filter((l) => l.id !== id),
    },
  });
}

  function updateVerticalOffset(id, value) {
  update({
    ...partitions,
    [partitionSide]: {
      ...activePartition,
      vertical: (activePartition.vertical || []).map((l) =>
        l.id === id ? { ...l, offset: Number(value) } : l
      ),
    },
  });
}

  function restoreSegment(lineId, segmentKey, orientation) {
  const listKey = orientation === "horizontal" ? "horizontal" : "vertical";

  update({
    ...partitions,
    [partitionSide]: {
      ...activePartition,
      [listKey]: (activePartition[listKey] || []).map((line) =>
        String(line.id) === String(lineId)
          ? {
              ...line,
              disabledSegments: (line.disabledSegments || []).filter(
                (x) => x !== segmentKey
              ),
            }
          : line
      ),
    },
  });
}

  return (
    <div className="space-y-4">

      <div>
  <label className="text-xs text-gray-500">Partition side</label>
  <select
    value={partitionSide}
    onChange={(e) => setPartitionSide(e.target.value)}
    className="w-full px-2 py-1 border rounded bg-white"
  >
    <option value="front">Front</option>
    <option value="back">Back</option>
    <option value="left">Left</option>
    <option value="right">Right</option>
    <option value="top">Top</option>
    <option value="bottom">Bottom</option>
  </select>
</div>

{partitionSide === "front" && (
  <div className="border rounded p-3 bg-white space-y-2">
    <div className="font-semibold text-sm">Front gaps</div>

    <div>
      <label className="text-xs text-gray-500">
        Outer gap / razmak od ivica
      </label>
      <input
        type="number"
        value={params.frontGapOuter ?? ""}
        onChange={(e) => updateCabinetParam("frontGapOuter", e.target.value)}
        className="w-full px-2 py-1 border rounded text-sm"
        placeholder={String(resolvedNode?.effectiveParameters?.frontGapOuter ?? 2)}
      />
    </div>

    <div>
      <label className="text-xs text-gray-500">
        Inner gap / razmak između front panela
      </label>
      <input
        type="number"
        value={params.frontGapInner ?? ""}
        onChange={(e) => updateCabinetParam("frontGapInner", e.target.value)}
        className="w-full px-2 py-1 border rounded text-sm"
        placeholder={String(resolvedNode?.effectiveParameters?.frontGapInner ?? 2)}
      />
    </div>
  </div>
)}

<div className="border rounded p-3 bg-white space-y-2">
  <div className="font-semibold text-sm">Shelves</div>

  <div>
    <label className="text-xs text-gray-500">Shelf count</label>
    <input
      type="number"
      value={params.shelfCount ?? ""}
      onChange={(e) => updateCabinetParam("shelfCount", e.target.value)}
      className="w-full px-2 py-1 border rounded text-sm"
      placeholder={String(resolvedNode?.effectiveParameters?.shelfCount ?? 0)}
    />
  </div>

  <div>
    <label className="text-xs text-gray-500">Shelf thickness</label>
    <input
      type="number"
      value={params.shelfThickness ?? ""}
      onChange={(e) => updateCabinetParam("shelfThickness", e.target.value)}
      className="w-full px-2 py-1 border rounded text-sm"
      placeholder={String(resolvedNode?.effectiveParameters?.shelfThickness ?? 18)}
    />
  </div>
</div>

      <div>
        <h3 className="font-semibold">{partitionSide.toUpperCase()} Partitions</h3>
        <div className="text-xs text-gray-500">
          Horizontal and vertical splits define front segments.
        </div>
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-semibold">Horizontal splits</h4>

       {(activePartition.horizontal || []).map((line) => (
          <div key={line.id} className="space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={line.offset}
                onChange={(e) => updateOffset(line.id, e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
              />
              <button
                type="button"
                onClick={() => removeHorizontal(line.id)}
                className="text-xs text-red-600"
              >
                remove
              </button>
            </div>

            {(line.disabledSegments || []).length > 0 && (
              <div className="flex flex-wrap gap-1 ml-1">
                {(line.disabledSegments || []).map((seg) => (
                  <button
                    key={seg}
                    type="button"
                    onClick={() => restoreSegment(line.id, seg, "horizontal")}
                    className="text-[10px] px-2 py-0.5 border rounded bg-yellow-50"
                  >
                    restore {seg}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={addHorizontal}
          className="w-full px-3 py-2 border rounded bg-white text-sm"
        >
          + Add horizontal split
        </button>
      </div>

      <div className="border-t pt-3 space-y-2">
        <h4 className="text-sm font-semibold">Vertical splits</h4>

       {(activePartition.vertical || []).map((line) => (
          <div key={line.id} className="space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={line.offset}
                onChange={(e) => updateVerticalOffset(line.id, e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
              />
              <button
                type="button"
                onClick={() => removeVertical(line.id)}
                className="text-xs text-red-600"
              >
                remove
              </button>
            </div>

            {(line.disabledSegments || []).length > 0 && (
              <div className="flex flex-wrap gap-1 ml-1">
                {(line.disabledSegments || []).map((seg) => (
                  <button
                    key={seg}
                    type="button"
                    onClick={() => restoreSegment(line.id, seg, "vertical")}
                    className="text-[10px] px-2 py-0.5 border rounded bg-yellow-50"
                  >
                    restore {seg}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={addVertical}
          className="w-full px-3 py-2 border rounded bg-white text-sm"
        >
          + Add vertical split
        </button>
      </div>
    </div>
  );
}

function FrontSegmentPanel({ resolvedNode, definitions = [], onChangeNode }) {
  const [localOverrides, setLocalOverrides] = useState({});

  const side = resolvedNode?.partitionSide || "front";
  const isShelf = side === "shelf";

  const editableParameterKeys = useMemo(() => {
  const keys = resolvedNode?.editableParameterKeys || resolvedNode?.ownParameterKeys || [];

  return keys.filter((key) =>
    [
      "trimLeft",
      "trimRight",
      "trimTop",
      "trimBottom",
      "offsetX",
      "offsetY",
      "offsetZ",
      "hingeDefinitionId",
      "handleDefinitionId",
      "hingeSide",
      "hingeCount",
      "hingeTopOffset",
      "hingeBottomOffset",
      "handleSide",
      "handleOffsetX",
      "handleOffsetY",
      "handleOffsetZ",
    ].includes(key)
  );
}, [resolvedNode]);

 useEffect(() => {
  let existing = {};

  if (side === "shelf" && resolvedNode?.panelKey) {
    existing =
      resolvedNode?.parentShelfOverrides?.[resolvedNode.panelKey]
        ?.parameterOverrides || {};
  } else if (resolvedNode?.panelKey) {
    existing =
      resolvedNode?.parentPartitions?.[side]?.panelOverrides?.[
        resolvedNode.panelKey
      ]?.parameterOverrides || {};
  } else if (resolvedNode?.overrideKey) {
    existing =
      resolvedNode?.parentChildDefinitionOverrides?.[resolvedNode.overrideKey]
        ?.parameterOverrides || {};
  }

  setLocalOverrides(existing);
}, [
  side,
  resolvedNode?.panelKey,
  resolvedNode?.overrideKey,
  resolvedNode?.parentGeneratedFromId,
  JSON.stringify(resolvedNode?.parentPartitions || {}),
  JSON.stringify(resolvedNode?.parentShelfOverrides || {}),
  JSON.stringify(resolvedNode?.parentChildDefinitionOverrides || {}),
]);

  const replaceOptions = useMemo(() => {
    return (definitions || []).filter((d) => {
      const category = String(d.category || "").toLowerCase();
      return (
        category.startsWith("front.") ||
        category.startsWith("drawer.") ||
        category.startsWith("board.") ||
        category.startsWith("glass.")
      );
    });
  }, [definitions]);

  const isDoorFront = String(resolvedNode?.category || "").startsWith("front.door");

const hardwareOptions = definitions.filter(
  (d) => d.nodeType === "hardware" || d.node_type === "hardware"
);

const effective = resolvedNode?.effectiveParameters || {};

  function updatePanelOverride(nextPatch) {
  const parentId = resolvedNode?.parentGeneratedFromId;
  const panelKey = resolvedNode?.panelKey;
  const overrideKey = resolvedNode?.overrideKey;
  const parentPartitions = resolvedNode?.parentPartitions || {};

  if (!parentId) return;

  if (side === "shelf" && panelKey) {
    const currentShelfOverrides = resolvedNode?.parentShelfOverrides || {};
    const current = currentShelfOverrides?.[panelKey] || {};

    onChangeNode?.({
      id: parentId,
      shelfOverrides: {
        ...currentShelfOverrides,
        [panelKey]: {
          ...current,
          ...nextPatch,
        },
      },
    });

    return;
  }

  if (!panelKey && overrideKey) {
    const currentOverrides = resolvedNode.parentChildDefinitionOverrides || {};
    const current = currentOverrides[overrideKey] || {};

    const nextOverrides = {
      ...currentOverrides,
      [overrideKey]: {
        ...current,
        definitionId: current.definitionId || resolvedNode.definitionId,
        ...nextPatch,
      },
    };

    onChangeNode?.({
      id: parentId,
      childDefinitionOverrides: nextOverrides,
      parentChildDefinitionOverrides: nextOverrides,
    });

    return;
  }

  if (!panelKey) return;

  const current =
    parentPartitions?.[side]?.panelOverrides?.[panelKey] || {};

  onChangeNode?.({
    id: parentId,
    partitions: {
      ...parentPartitions,
      [side]: {
        ...(parentPartitions[side] || {}),
        panelOverrides: {
          ...(parentPartitions[side]?.panelOverrides || {}),
          [panelKey]: {
            ...current,
            ...nextPatch,
          },
        },
      },
    },
  });
}

  function changeDefinition(definitionId) {
    updatePanelOverride({
      definitionId: Number(definitionId),
    });
  }

  function updateSegmentMaterialAssignment(patch) {
  const current =
    resolvedNode?.parentPartitions?.[side]?.panelOverrides?.[
  resolvedNode?.panelKey
] || {};

  const currentAssignments =
    current.materialAssignments || resolvedNode?.materialAssignments || {};

  updatePanelOverride({
    materialAssignments: {
      ...currentAssignments,
      body: {
        ...(currentAssignments.body || {}),
        ...patch,
      },
    },
  });
}

  function updateParam(key, value) {
    const next = {
      ...localOverrides,
      [key]: parseValue(value),
    };

    setLocalOverrides(next);

    updatePanelOverride({
      parameterOverrides: next,
    });
  }

  function removeParam(key) {
    const next = { ...localOverrides };
    delete next[key];

    setLocalOverrides(next);

    updatePanelOverride({
      parameterOverrides: next,
    });
  }

  function addParam() {
    const key = prompt("Parameter key?");
    if (!key) return;

    updateParam(key, "");
  }

  return (
    <div
    className="h-full flex flex-col bg-white"
    onPointerDown={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
  >
      <div className="border-b px-3 py-3 bg-blue-50">
        <div className="text-xs uppercase text-blue-600 font-semibold">
          {side.toUpperCase()} Segment
        </div>
        <div className="font-semibold text-sm">
          {resolvedNode?.name || resolvedNode?.panelKey}
        </div>
        <div className="text-xs text-slate-500">
          panelKey: <span className="font-mono">{resolvedNode?.panelKey}</span>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3 space-y-4">

{isShelf && (
  <div className="border rounded p-3 bg-white space-y-3">
    <div className="grid grid-cols-2 gap-2">
  <div>
    <label className="text-xs text-slate-500">Inset X left/right</label>
    <input
      type="number"
      value={localOverrides.insetX ?? ""}
      onChange={(e) => updateParam("insetX", e.target.value)}
      className="w-full px-2 py-1 border rounded text-sm bg-white"
      placeholder="box default"
    />
  </div>

  <div>
    <label className="text-xs text-slate-500">Inset Z front/back</label>
    <input
      type="number"
      value={localOverrides.insetZ ?? ""}
      onChange={(e) => updateParam("insetZ", e.target.value)}
      className="w-full px-2 py-1 border rounded text-sm bg-white"
      placeholder="box default"
    />
  </div>
</div>

<div>
  <label className="text-xs text-slate-500">Offset Y up/down</label>
  <input
    type="number"
    value={localOverrides.offsetY ?? ""}
    onChange={(e) => updateParam("offsetY", e.target.value)}
    className="w-full px-2 py-1 border rounded text-sm bg-white"
    placeholder="0"
  />
</div>
  </div>
)}

        <div className="border rounded p-3 bg-white space-y-2">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Element type
            </label>

            {isDoorFront && (
  <div className="border rounded p-3 bg-white space-y-3">
    <div>
      <div className="font-semibold text-sm">Door hardware</div>
      <div className="text-xs text-slate-500">
        Defaults come from Door definition. Changes here are segment overrides.
      </div>
    </div>

    <div className="border rounded p-3 bg-white space-y-2">
  <label className="block text-xs font-medium text-slate-500">
    Texture mapping
  </label>

  <select
    value={resolvedNode?.materialAssignments?.body?.uv_mode || "local"}
    onChange={(e) =>
      updateSegmentMaterialAssignment({
        uv_mode: e.target.value,
      })
    }
    className="w-full px-3 py-2 border rounded text-sm bg-white"
  >
    <option value="local">Local - svaka ploča posebno</option>
    <option value="global">Global - tekstura se nastavlja preko fronta</option>
  </select>
</div>

    <div>
      <label className="text-xs text-slate-500">Hinge</label>
      <select
        value={localOverrides.hingeDefinitionId ?? effective.hingeDefinitionId ?? ""}
        onChange={(e) => updateParam("hingeDefinitionId", e.target.value)}
        className="w-full px-2 py-1 border rounded text-sm bg-white"
      >
        <option value="">No hinge</option>
        {hardwareOptions.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name || d.code} — {d.category}
          </option>
        ))}
      </select>
    </div>

    <div>
      <label className="text-xs text-slate-500">Hinge side</label>
      <select
        value={localOverrides.hingeSide ?? effective.hingeSide ?? "left"}
        onChange={(e) => updateParam("hingeSide", e.target.value)}
        className="w-full px-2 py-1 border rounded text-sm bg-white"
      >
        <option value="left">left</option>
        <option value="right">right</option>
      </select>
    </div>

    <div>
      <label className="text-xs text-slate-500">Hinge count</label>
      <input
        type="number"
        value={localOverrides.hingeCount ?? effective.hingeCount ?? 2}
        onChange={(e) => updateParam("hingeCount", e.target.value)}
        className="w-full px-2 py-1 border rounded text-sm bg-white"
      />
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-slate-500">Top offset</label>
        <input
          type="number"
          value={localOverrides.hingeTopOffset ?? effective.hingeTopOffset ?? 100}
          onChange={(e) => updateParam("hingeTopOffset", e.target.value)}
          className="w-full px-2 py-1 border rounded text-sm bg-white"
        />
      </div>

      <div>
        <label className="text-xs text-slate-500">Bottom offset</label>
        <input
          type="number"
          value={localOverrides.hingeBottomOffset ?? effective.hingeBottomOffset ?? 100}
          onChange={(e) => updateParam("hingeBottomOffset", e.target.value)}
          className="w-full px-2 py-1 border rounded text-sm bg-white"
        />
      </div>
    </div>
  </div>
)}

            <select
              value={resolvedNode?.definitionId || ""}
              onChange={(e) => changeDefinition(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm bg-white"
            >
              <option value="">Select element...</option>
              {replaceOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.code} — {d.category}
                </option>
              ))}
            </select>
          </div>

          <div className="text-xs text-slate-500">
            Segment automatically passes width / height / depth from the grid.
          </div>
        </div>

        

        <div className="border rounded p-3 bg-white space-y-3">
          <div className="flex items-center justify-between">
           <div className="border rounded p-3 bg-white space-y-3">
  <div>
    <div className="font-semibold text-sm">Panel parameters</div>
    <div className="text-xs text-slate-500">
      Local overrides for this selected panel.
    </div>
  </div>

  {editableParameterKeys.length ? (
    <div className="space-y-2">
      {editableParameterKeys.map((key) => (
        <div key={key}>
          <label className="text-xs text-slate-500">{key}</label>
          <input
            value={localOverrides[key] ?? ""}
            onChange={(e) => updateParam(key, e.target.value)}
            className="w-full px-2 py-1 border rounded text-sm bg-white"
            placeholder={String(effective?.[key] ?? "inherited")}
          />
        </div>
      ))}
    </div>
  ) : (
    <div className="text-xs text-slate-400 border rounded p-3 bg-slate-50">
      No editable parameters for this element.
    </div>
  )}
</div>            
          </div>
          <button
              type="button"
              onClick={addParam}
              className="px-2 py-1 rounded border bg-white text-xs"
            >
              + Add
            </button>

          {Object.entries(localOverrides || {}).length ? (
  <div className="space-y-2">
    {Object.entries(localOverrides || {}).map(([key, value]) => {
      const hardwareOptions = definitions.filter(
        (d) => d.nodeType === "hardware" || d.node_type === "hardware"
      );

      return (
        <div key={key} className="border rounded p-2 bg-slate-50">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-slate-500">
              {key}
            </label>

            <button
              type="button"
              onClick={() => removeParam(key)}
              className="text-xs text-red-600"
            >
              remove
            </button>
          </div>

          {key === "hingeDefinitionId" || key === "handleDefinitionId" ? (
            <select
              value={value || ""}
              onChange={(e) => updateParam(key, e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm bg-white"
            >
              <option value="">No hardware</option>

              {hardwareOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.code} — {d.category}
                </option>
              ))}
            </select>
          ) : key === "hingeSide" ? (
            <select
              value={value || "left"}
              onChange={(e) => updateParam(key, e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm bg-white"
            >
              <option value="left">left</option>
              <option value="right">right</option>
            </select>
          ) : key === "handleSide" ? (
            <select
              value={value || "opposite_hinge"}
              onChange={(e) => updateParam(key, e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm bg-white"
            >
              <option value="opposite_hinge">opposite_hinge</option>
              <option value="left">left</option>
              <option value="right">right</option>
            </select>
          ) : (
            <input
              value={value ?? ""}
              onChange={(e) => updateParam(key, e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm bg-white"
            />
          )}
        </div>
      );
    })}
  </div>
) : (
  <div className="text-xs text-slate-400 border rounded p-3 bg-slate-50">
    No segment overrides.
  </div>
)}
        </div>

        <ResolvedView resolvedNode={resolvedNode} />
      </div>
    </div>
  );
}

function MaterialPanel({ node, resolvedNode, onChange }) {
  const activeNode = node || resolvedNode;
  const [materials, setMaterials] = useState([]);
  const [applyToChildren, setApplyToChildren] = useState(true);

  const assignments = activeNode?.materialAssignments || {};
  const body = assignments.body || {};
  const [useIndividualEdges, setUseIndividualEdges] = useState(
  Boolean(assignments.edges && Object.keys(assignments.edges).length)
);

useEffect(() => {
  setUseIndividualEdges(
    Boolean(assignments.edges && Object.keys(assignments.edges).length)
  );
}, [activeNode?.id]);

  function updateAssignmentSlot(slotKey, patch) {
  const currentAssignments = activeNode?.materialAssignments || {};
  const currentSlot = currentAssignments[slotKey] || {};

  const nextAssignments = {
    ...currentAssignments,
    [slotKey]: {
      ...currentSlot,
      ...patch,
    },
  };

  if (node?.id) {
    onChange?.({
      id: node.id,
      applyMaterialToChildren: applyToChildren,
      materialAssignments: nextAssignments,
    });
    return;
  }

  if (resolvedNode?.panelKey && resolvedNode?.parentGeneratedFromId) {

if (
  resolvedNode?.partitionSide === "shelf" &&
  resolvedNode?.panelKey &&
  resolvedNode?.parentGeneratedFromId
) {
  const shelfKey = resolvedNode.panelKey;
  const currentShelfOverrides =
    resolvedNode?.parentShelfOverrides || resolvedNode?.shelfOverrides || {};

  const current =
    currentShelfOverrides?.[shelfKey] || {};

  onChange?.({
    id: resolvedNode.parentGeneratedFromId,
    shelfOverrides: {
      ...currentShelfOverrides,
      [shelfKey]: {
        ...current,
        definitionId: current.definitionId || resolvedNode.definitionId,
        materialAssignments: nextAssignments,
      },
    },
  });

  return;
}

    const panelKey = resolvedNode.panelKey;
    const parentPartitions = resolvedNode.parentPartitions || {};
    const side = resolvedNode?.partitionSide || "front";
    const currentPanelOverride =
  parentPartitions?.[side]?.panelOverrides?.[panelKey] || {};

onChange?.({
  id: resolvedNode.parentGeneratedFromId,
  partitions: {
    ...parentPartitions,
    [side]: {
      ...(parentPartitions[side] || {}),
      panelOverrides: {
        ...(parentPartitions[side]?.panelOverrides || {}),
        [panelKey]: {
          ...currentPanelOverride,
          definitionId:
            currentPanelOverride.definitionId || resolvedNode.definitionId,
          materialAssignments: nextAssignments,
        },
      },
    },
  },
});
    return;
  }

  if (resolvedNode?.parentGeneratedFromId && resolvedNode?.overrideKey) {
    const key = resolvedNode.overrideKey;
    const currentOverrides = resolvedNode.parentChildDefinitionOverrides || {};
    const current = currentOverrides[key] || {};

    const nextChildOverrides = {
      ...currentOverrides,
      [key]: {
        ...current,
        definitionId: current.definitionId || resolvedNode.definitionId,
        materialAssignments: nextAssignments,
      },
    };

    onChange?.({
      id: resolvedNode.parentGeneratedFromId,
      childDefinitionOverrides: nextChildOverrides,
      parentChildDefinitionOverrides: nextChildOverrides,
    });
    return;
  }

  alert("Select a real project node first.");
}

function update(patch) {
  updateAssignmentSlot("body", patch);
}

function getMaterialSource(slotKey) {
  const slot = assignments?.[slotKey];

  if (slot?.materialId) return "Override";

  return "Project default / inherited";
}

function resetMaterialSlot(slotKey) {
  const nextAssignments = { ...(assignments || {}) };
  delete nextAssignments[slotKey];

  if (node?.id) {
    onChange?.({
      id: node.id,
      materialAssignments: nextAssignments,
    });
  }
}


function MaterialSlot({ label, slotKey }) {
  const slot = assignments[slotKey] || {};

  return (
    <div className="border rounded p-3 bg-white space-y-2">
      <div className="font-semibold text-sm">{label}</div>

      <div className="flex items-center justify-between text-xs">
  <span className="text-slate-500">
    Source: {getMaterialSource(slotKey)}
  </span>

  {assignments?.[slotKey]?.materialId && (
    <button
      type="button"
      onClick={() => resetMaterialSlot(slotKey)}
      className="text-red-600"
    >
      reset
    </button>
  )}
</div>

      <select
        value={slot.materialId || ""}
        onChange={(e) =>
          onChange?.({
            id: activeNode.id,
            materialAssignments: {
              ...assignments,
              [slotKey]: {
                ...slot,
                materialId: Number(e.target.value),
              },
            },
          })
        }
        className="w-full px-2 py-1 border rounded"
      >
        <option value="">Select material</option>
        {materials.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>

      <select
        value={slot.uv_mode || "local"}
        onChange={(e) =>
          onChange?.({
            id: activeNode.id,
            materialAssignments: {
              ...assignments,
              [slotKey]: {
                ...slot,
                uv_mode: e.target.value,
              },
            },
          })
        }
        className="w-full px-2 py-1 border rounded bg-white"
      >
        <option value="local">Local</option>
        <option value="global">Global</option>
      </select>

      <input
  type="number"
  step="0.1"
  value={slot.edgeThicknessMm ?? 1}
  onChange={(e) =>
    onChange?.({
      id: activeNode.id,
      materialAssignments: {
        ...assignments,
        [slotKey]: {
          ...slot,
          edgeThicknessMm: Number(e.target.value),
        },
      },
    })
  }
  className="w-full px-2 py-1 border rounded"
  placeholder="Edge thickness mm"
/>

{/* {slotKey.toLowerCase().includes("edge") && (
  <div className="border-t pt-2 space-y-2">
    <div className="text-xs font-semibold text-gray-500">
      Individual edges
    </div>

    {["top", "bottom", "left", "right"].map((edgeKey) => {
      const groupKey = `${slotKey}s`;
      const edgeSlot = assignments[groupKey]?.[edgeKey] || {};

      return (
        <div key={edgeKey} className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] text-gray-500">{edgeKey} material</label>
            <select
              value={edgeSlot.materialId || ""}
              onChange={(e) =>
                onChange?.({
                  id: activeNode.id,
                  materialAssignments: {
                    ...assignments,
                    [groupKey]: {
                      ...(assignments[groupKey] || {}),
                      [edgeKey]: {
                        ...edgeSlot,
                        materialId: Number(e.target.value),
                      },
                    },
                  },
                })
              }
              className="w-full px-2 py-1 border rounded"
            >
              <option value="">Use group edge</option>
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] text-gray-500">thickness</label>
            <input
              type="number"
              step="0.1"
              value={edgeSlot.edgeThicknessMm ?? ""}
              onChange={(e) =>
                onChange?.({
                  id: activeNode.id,
                  materialAssignments: {
                    ...assignments,
                    [groupKey]: {
                      ...(assignments[groupKey] || {}),
                      [edgeKey]: {
                        ...edgeSlot,
                        edgeThicknessMm: Number(e.target.value),
                      },
                    },
                  },
                })
              }
              className="w-full px-2 py-1 border rounded"
              placeholder="default"
            />
          </div>
        </div>
      );
    })}
  </div>
)} */}
    </div>
  );
}

  useEffect(() => {
  async function load() {
    const res = await interiorLibraryAdminApi.listMaterials();
    setMaterials(res.items || []);
  }

  load();
}, []);

const category = String(
  activeNode?.category ||
  activeNode?.definition?.category ||
  resolvedNode?.category ||
  resolvedNode?.definition?.category ||
  ""
).toLowerCase();

const isCabinetBox = category.includes("cabinet.box");

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Material</h3>
        {isCabinetBox && (
  <div className="border rounded p-3 bg-blue-50 text-xs space-y-1">
    <div className="font-semibold text-sm mb-2">
      Box material mapping
    </div>

    <div>Corpus → left/right/top/bottom</div>
    <div className="ml-2 text-gray-600">
      Board: {materials.find(m => m.id == assignments?.corpusBoard?.materialId)?.name || "—"}
    </div>
    <div className="ml-2 text-gray-600">
      Edge: {materials.find(m => m.id == assignments?.corpusEdge?.materialId)?.name || "—"}
    </div>

    <div className="mt-2">Front → doors / drawers</div>
    <div className="ml-2 text-gray-600">
      Board: {materials.find(m => m.id == assignments?.frontBoard?.materialId)?.name || "—"}
    </div>
    <div className="ml-2 text-gray-600">
      Edge: {materials.find(m => m.id == assignments?.frontEdge?.materialId)?.name || "—"}
    </div>

    <div className="mt-2">Back panel</div>
    <div className="ml-2 text-gray-600">
      Board: {materials.find(m => m.id == assignments?.backBoard?.materialId)?.name || "—"}
    </div>

    <div className="mt-2">Shelves</div>
    <div className="ml-2 text-gray-600">
      Board: {materials.find(m => m.id == assignments?.shelfBoard?.materialId)?.name || "—"}
    </div>

    <div className="mt-2">Special</div>
    <div className="ml-2 text-gray-600">
      Glass: {materials.find(m => m.id == assignments?.glassFront?.materialId)?.name || "—"}
    </div>
    <div className="ml-2 text-gray-600">
      Alu: {materials.find(m => m.id == assignments?.aluFrontProfile?.materialId)?.name || "—"}
    </div>
  </div>
)}

{isCabinetBox && (
  <div className="space-y-3">
    <MaterialSlot label="Corpus board" slotKey="corpusBoard" />
    <MaterialSlot label="Corpus edge banding" slotKey="corpusEdge" />

    <MaterialSlot label="Front board" slotKey="frontBoard" />
    <MaterialSlot label="Front edge banding" slotKey="frontEdge" />

    <MaterialSlot label="Back board" slotKey="backBoard" />
    <MaterialSlot label="Back edge banding" slotKey="backEdge" />

    <MaterialSlot label="Shelf board" slotKey="shelfBoard" />
    <MaterialSlot label="Shelf edge banding" slotKey="shelfEdge" />

    <MaterialSlot label="Glass front" slotKey="glassFront" />
    <MaterialSlot label="Alu profile front" slotKey="aluFrontProfile" />
  </div>
)}

      </div>

      <div>
  
       

{!isCabinetBox && (
  <>
    <div>
      <label className="text-xs text-gray-500">Material</label>
      <select
        value={body.materialId || ""}
        onChange={(e) => update({ materialId: Number(e.target.value) })}
        className="w-full px-2 py-1 border rounded"
      >
        <option value="">Select material</option>
        {materials.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
    </div>

    <div>
  <label className="text-xs text-gray-500">Edge material override</label>
  <select
    value={assignments.edge?.materialId || ""}
    onChange={(e) =>
  updateAssignmentSlot("edge", {
    materialId: Number(e.target.value),
    edgeThicknessMm: assignments.edge?.edgeThicknessMm ?? 1,
    textureRotation: assignments.edge?.textureRotation ?? 90,
  })
}
    className="w-full px-2 py-1 border rounded"
  >
    <option value="">Use default edge</option>
    {materials.map((m) => (
      <option key={m.id} value={m.id}>
        {m.name}
      </option>
    ))}
  </select>

  <div>
  <label className="text-xs text-gray-500">Edge thickness override mm</label>
  <input
    type="number"
    step="0.1"
    value={assignments.edge?.edgeThicknessMm ?? ""}
    onChange={(e) =>
      updateAssignmentSlot("edge", {
        ...(assignments.edge || {}),
        edgeThicknessMm: Number(e.target.value),
      })
    }
    className="w-full px-2 py-1 border rounded"
    placeholder="Use default"
  />
</div>

<label className="flex items-center gap-2 text-xs text-gray-600 mt-2">
  <input
    type="checkbox"
    checked={useIndividualEdges}
    onChange={(e) => setUseIndividualEdges(e.target.checked)}
  />
  Set each edge separately
</label>

{useIndividualEdges && (
  <div className="border rounded p-3 bg-white space-y-3">
    <div className="font-semibold text-sm">Individual edge overrides</div>

    {["top", "bottom", "left", "right"].map((edgeKey) => {
      const edgeSlot = assignments.edges?.[edgeKey] || {};

      return (
        <div key={edgeKey} className="border rounded p-2 bg-slate-50 space-y-2">
          <div className="text-xs font-semibold uppercase text-gray-500">
            {edgeKey}
          </div>

          <div>
            <label className="text-[11px] text-gray-500">Material</label>
            <select
              value={edgeSlot.materialId || ""}
              onChange={(e) =>
                updateAssignmentSlot("edges", {
                  ...(assignments.edges || {}),
                  [edgeKey]: {
                    ...edgeSlot,
                    materialId: e.target.value ? Number(e.target.value) : undefined,
                  },
                })
              }
              className="w-full px-2 py-1 border rounded"
            >
              <option value="">Use default edge</option>
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] text-gray-500">Thickness mm</label>
            <input
              type="number"
              step="0.1"
              value={edgeSlot.edgeThicknessMm ?? ""}
              onChange={(e) =>
                updateAssignmentSlot("edges", {
                  ...(assignments.edges || {}),
                  [edgeKey]: {
                    ...edgeSlot,
                    edgeThicknessMm: Number(e.target.value),
                  },
                })
              }
              className="w-full px-2 py-1 border rounded"
              placeholder="default"
            />
          </div>
        </div>
      );
    })}
  </div>
)}

</div>

    <div>
      <label className="text-xs text-gray-500">Rotation (deg)</label>
      <input
        type="number"
        value={body.textureRotation || 0}
        onChange={(e) => update({ textureRotation: Number(e.target.value) })}
        className="w-full px-2 py-1 border rounded"
      />
    </div>

    <div>
      <label className="text-xs text-gray-500">Texture mapping</label>
      <select
        value={body.uv_mode || "local"}
        onChange={(e) => update({ uv_mode: e.target.value })}
        className="w-full px-2 py-1 border rounded bg-white"
      >
        <option value="local">Local - svaka ploča posebno</option>
        <option value="global">Global - tekstura se nastavlja</option>
      </select>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500">Scale X</label>
        <input
          type="number"
          value={body.textureScaleX || 1}
          onChange={(e) => update({ textureScaleX: Number(e.target.value) })}
          className="w-full px-2 py-1 border rounded"
        />
      </div>

      <div>
        <label className="text-xs text-gray-500">Scale Y</label>
        <input
          type="number"
          value={body.textureScaleY || 1}
          onChange={(e) => update({ textureScaleY: Number(e.target.value) })}
          className="w-full px-2 py-1 border rounded"
        />
      </div>
    </div>
  </>
)}
   </div>
    </div>
  );
}



export default function RightPanel({
  selectedNode,
  resolvedNode,
  definitions = [],
  projectParameters,
  setProjectParameters,
  onChangeNode,
  selectedPartition,
}) {
  const [tab, setTab] = useState("quick");

  const PANEL_ROLES = ["front", "back", "left", "right", "top", "bottom"];

const isShelfSegment =
  Boolean(resolvedNode?.panelKey) &&
  Boolean(resolvedNode?.parentGeneratedFromId) &&
  resolvedNode?.partitionSide === "shelf";

const isPartitionPanel =
  Boolean(resolvedNode?.panelKey) &&
  Boolean(resolvedNode?.parentGeneratedFromId) &&
  resolvedNode?.partitionSide !== "shelf";

const isGeneratedSidePanel =
  Boolean(resolvedNode?.parentGeneratedFromId) &&
  Boolean(resolvedNode?.overrideKey) &&
  PANEL_ROLES.includes(String(resolvedNode?.overrideKey || "").toLowerCase());

const isEditablePanel =
  isPartitionPanel || isShelfSegment || isGeneratedSidePanel;

 useEffect(() => {
  if (selectedPartition) return;

  if (isEditablePanel) {
    setTab("quick");
    return;
  }

  if (resolvedNode) setTab("resolved");
}, [
  resolvedNode?.id,
  resolvedNode?.panelKey,
  resolvedNode?.overrideKey,
  resolvedNode?.parentGeneratedFromId,
  selectedPartition,
  isEditablePanel,
]);

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="border-b px-3 pt-3">
        <div className="font-semibold">Parameter Manager</div>
        <div className="text-xs text-gray-500 mb-2">
          Project → parent → definition → bindings → overrides
        </div>

        <div className="flex gap-1 overflow-x-auto">
          <TabButton active={tab === "quick"} onClick={() => setTab("quick")}>
            Quick
          </TabButton>
          <TabButton active={tab === "project"} onClick={() => setTab("project")}>
            Project
          </TabButton>
          <TabButton active={tab === "instance"} onClick={() => setTab("instance")}>
            Instance
          </TabButton>
          <TabButton active={tab === "material"} onClick={() => setTab("material")}>
            Material
          </TabButton>
          <TabButton active={tab === "bindings"} onClick={() => setTab("bindings")}>
            Bindings
          </TabButton>
          <TabButton active={tab === "overrides"} onClick={() => setTab("overrides")}>
            Overrides
          </TabButton>
          <TabButton active={tab === "resolved"} onClick={() => setTab("resolved")}>
            Resolved
          </TabButton>
          <TabButton active={tab === "flow"} onClick={() => setTab("flow")}>
            Flow
          </TabButton>
          <TabButton active={tab === "partitions"} onClick={() => setTab("partitions")}>
            Partitions
          </TabButton>
          <TabButton active={tab === "replace"} onClick={() => setTab("replace")}>
            Replace
          </TabButton>
          <TabButton active={tab === "debug"} onClick={() => setTab("debug")}>
            Debug
          </TabButton>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {tab === "quick" && isEditablePanel && (
  <div className="space-y-4">
    <FrontSegmentPanel
      resolvedNode={resolvedNode}
      definitions={definitions}
      onChangeNode={onChangeNode}
    />

    <MaterialPanel
      node={selectedNode}
      resolvedNode={resolvedNode}
      onChange={onChangeNode}
    />
  </div>
)}
{/* 
{tab === "quick" && !isEditablePanel && (
  <div className="space-y-4">
    <MaterialPanel
      node={selectedNode}
      resolvedNode={resolvedNode}
      onChange={onChangeNode}
    />
    <ResolvedView resolvedNode={resolvedNode} />
  </div>
)} */}
        {tab === "project" && (
          <ProjectParametersPanel
            projectParameters={projectParameters}
            setProjectParameters={setProjectParameters}
          />
        )}

        {tab === "instance" && (
  <InstanceTransformInspector
    node={selectedNode || resolvedNode}
    resolvedNode={resolvedNode}
    onChange={onChangeNode}
  />
)}

        {tab === "material" && (
          <MaterialPanel
  node={selectedNode}
  resolvedNode={resolvedNode}
  onChange={onChangeNode}
/>
        )}

        {tab === "bindings" && (
          <InstanceParameterBindingsInspector
            node={selectedNode}
            onChange={onChangeNode}
          />
        )}

        {tab === "overrides" && (
  <InstanceParameterOverridesInspector
    node={selectedNode}
    resolvedNode={resolvedNode}
    onChange={onChangeNode}
  />
)}

        {tab === "resolved" && <ResolvedView resolvedNode={resolvedNode} />}

        {tab === "flow" && <ParameterFlowView resolvedNode={resolvedNode} />}

        {tab === "partitions" && (
          <PartitionsPanel
            node={selectedNode}
            resolvedNode={resolvedNode}
            onChange={onChangeNode}
          />
        )}

        {tab === "replace" && (
          <div className="space-y-3">
            <h3 className="font-semibold">Replace Element</h3>
            <div className="text-sm text-slate-500">
              Select generated side/top/back board or real node.
            </div>
          </div>
        )}

        {tab === "debug" && (
          <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto">
            {JSON.stringify(resolvedNode, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}